import type { LeagueKey } from '@parlay-picker/shared/league';
import { hasPlayerProps } from '@parlay-picker/shared/league';
import {
  findTeam,
  getTeamLogo,
  getTeamsForLeague,
  type TeamInfo,
} from '@parlay-picker/shared/teams';
import { Search, UsersRound, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LineupView } from './LineupView';

// =============================================================================
// Main Panel (thin shell — chrome only, delegates content to LineupView)
// =============================================================================

export interface RosterIntelPanelProps {
  teamAbbr?: string;
  opponentAbbr?: string;
  gameDate?: string;
  onClose?: () => void;
  /** League key — college leagues show an unsupported message instead of roster data */
  leagueKey: LeagueKey;
}

export function RosterIntelPanel({
  teamAbbr,
  opponentAbbr,
  gameDate,
  onClose,
  leagueKey,
}: RosterIntelPanelProps) {
  const [activeTeam, setActiveTeam] = useState(teamAbbr ?? null);
  const [showSearch, setShowSearch] = useState(!teamAbbr);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Compute effective opponent: when switching between the two matchup teams, flip the opponent
  const effectiveOpponent =
    activeTeam === teamAbbr ? opponentAbbr : activeTeam === opponentAbbr ? teamAbbr : undefined;

  const teamLogo = activeTeam && leagueKey ? getTeamLogo(activeTeam, leagueKey) : null;
  const teamName = activeTeam && leagueKey ? findTeam(activeTeam, leagueKey)?.name : null;

  // Team search: a league with no team map has no teams to search
  const allTeams: (TeamInfo & { name: string })[] = Object.entries(
    getTeamsForLeague(leagueKey) ?? {},
  )
    .filter(([name]) => !name.startsWith('LA ')) // Deduplicate LA teams
    .map(([name, info]) => ({ ...info, name }));

  const filteredTeams = useMemo(
    () =>
      searchQuery
        ? allTeams.filter(
            (t) =>
              t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              t.abbr.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : allTeams,
    [allTeams, searchQuery],
  );

  const handleSelectTeam = (abbr: string) => {
    setActiveTeam(abbr);
    setShowSearch(false);
    setSearchQuery('');
  };

  const content = (
    <div
      className="fixed inset-0 z-70 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-surface border border-theme-border rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-theme-border shrink-0">
          {activeTeam ? (
            <>
              {teamLogo && (
                <img
                  src={teamLogo}
                  alt={activeTeam}
                  loading="lazy"
                  decoding="async"
                  className="w-8 h-8 object-contain"
                />
              )}
              <div className="flex-1">
                <div className="font-semibold text-theme-text">{teamName ?? activeTeam}</div>
                {opponentAbbr && (activeTeam === teamAbbr || activeTeam === opponentAbbr) && (
                  <div className="flex gap-1 mt-1">
                    {[teamAbbr, opponentAbbr].map((abbr) => (
                      <button
                        key={abbr}
                        type="button"
                        onClick={() => setActiveTeam(abbr!)}
                        className={`text-xs px-2 py-0.5 rounded-full border transition ${
                          activeTeam === abbr
                            ? 'bg-theme-primary-fill border-theme-primary text-theme-on-primary-fill'
                            : 'border-theme-border text-theme-text-secondary hover:text-theme-text'
                        }`}
                      >
                        {abbr}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <UsersRound className="w-6 h-6 text-theme-primary" />
              <div className="flex-1 font-semibold text-theme-text">Roster Intel</div>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              if (!activeTeam) {
                // In browse mode, search is always visible — clicking goes back to browse
                setSearchQuery('');
                searchInputRef.current?.focus();
                return;
              }
              setShowSearch((v) => !v);
              setSearchQuery('');
              setTimeout(() => searchInputRef.current?.focus(), 50);
            }}
            className={`p-1 rounded-lg transition ${
              showSearch
                ? 'text-theme-primary bg-theme-primary/10'
                : 'text-theme-text-tertiary hover:text-theme-text'
            }`}
            title="Search teams"
          >
            <Search className="w-5 h-5" />
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg text-theme-text-tertiary hover:text-theme-text transition"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Team Search Dropdown — only when a team is already loaded */}
        {activeTeam && showSearch && (
          <div className="border-b border-theme-border px-4 py-2 shrink-0">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search teams..."
              className="w-full px-3 py-1.5 text-sm rounded-lg bg-theme-surface-hover border border-theme-border text-theme-text placeholder:text-theme-text-tertiary focus:outline-none focus:border-theme-primary"
            />
            <div className="mt-2 max-h-48 overflow-y-auto space-y-0.5">
              {filteredTeams.map((team) => (
                <button
                  key={team.abbr}
                  type="button"
                  onClick={() => handleSelectTeam(team.abbr)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left transition ${
                    activeTeam === team.abbr
                      ? 'bg-theme-primary/10 text-theme-primary'
                      : 'hover:bg-theme-surface-hover text-theme-text'
                  }`}
                >
                  <img
                    src={team.logo}
                    alt={team.abbr}
                    loading="lazy"
                    decoding="async"
                    className="w-5 h-5 object-contain"
                  />
                  <span className="flex-1 text-sm truncate">{team.name}</span>
                  <span className="text-xs text-theme-text-tertiary font-mono">{team.abbr}</span>
                </button>
              ))}
              {filteredTeams.length === 0 && (
                <div className="text-xs text-theme-text-tertiary text-center py-2">
                  No teams found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto flex-1 py-1 pr-1">
          {/* Guard — roster intel requires player props (professional leagues only) */}
          {leagueKey && !hasPlayerProps(leagueKey) ? (
            <div className="p-8 text-center space-y-2">
              <UsersRound className="w-8 h-8 text-theme-text-tertiary mx-auto" />
              <p className="text-sm text-theme-text-secondary">
                Roster intel is available for leagues with player props only
              </p>
              <p className="text-xs text-theme-text-tertiary">
                College roster data is not currently supported
              </p>
            </div>
          ) : (
            <>
              {/* Browse state — no team selected */}
              {!activeTeam && (
                <div className="px-4 py-4 space-y-3">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search teams..."
                    className="w-full px-3 py-2 text-sm rounded-lg bg-theme-surface-hover border border-theme-border text-theme-text placeholder:text-theme-text-tertiary focus:outline-none focus:border-theme-primary"
                  />
                  {!searchQuery && (
                    <div className="text-center pb-1">
                      <div className="text-sm text-theme-text-secondary">
                        Select a team to view rosters, injuries, and matchup intel
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-5 @2xl:grid-cols-6 gap-2">
                    {filteredTeams.map((team) => (
                      <button
                        key={team.abbr}
                        type="button"
                        onClick={() => handleSelectTeam(team.abbr)}
                        className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-theme-surface-hover transition group"
                        title={team.name}
                      >
                        <img
                          src={team.logo}
                          alt={team.abbr}
                          loading="lazy"
                          decoding="async"
                          className="w-8 h-8 object-contain"
                        />
                        <span className="text-xs text-theme-text-tertiary group-hover:text-theme-text font-mono">
                          {team.abbr}
                        </span>
                      </button>
                    ))}
                    {filteredTeams.length === 0 && (
                      <div className="col-span-full text-xs text-theme-text-tertiary text-center py-4">
                        No teams found
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Team content — delegate to LineupView */}
              {activeTeam && (
                <LineupView
                  teamAbbr={activeTeam}
                  opponentAbbr={effectiveOpponent}
                  gameDate={gameDate}
                  leagueKey={leagueKey}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
